import React, { useState, useMemo, useId } from 'react'
import { useMutation } from '@tanstack/react-query'
import api from '../services/api'
import MediaSelector from './MediaSelector'
import { getBackendUrl } from '../utils/backendUrl'
import Input from './Input'

const getFullImageUrl = (url) => {
  if (!url) return ''
  if (typeof url !== 'string') {
    if (typeof url === 'object' && url.url) return getFullImageUrl(url.url)
    return ''
  }
  if (url.startsWith('http') || url.startsWith('data:')) return url
  const backendUrl = getBackendUrl()
  return backendUrl ? `${backendUrl}${url}` : url
}

const normalizeValue = (val) => {
  if (!val) return { url: '', width: '', height: '', alt: '' }
  if (typeof val === 'string') return { url: val, width: '', height: '', alt: '' }
  return {
    url: val.url || '',
    width: val.width || '',
    height: val.height || '',
    alt: val.alt || '',
  }
}

const ImageUpload = ({
  value,
  onChange,
  onImageChange,
  imageUrl,
  className = '',
}) => {
  const normalizedValue = useMemo(() => normalizeValue(value || imageUrl), [value, imageUrl])
  const displayUrl = useMemo(() => getFullImageUrl(normalizedValue.url), [normalizedValue.url])
  const actualOnChange = onChange || onImageChange
  const [isUploading, setIsUploading] = useState(false)
  const [showMediaSelector, setShowMediaSelector] = useState(false)
  const inputId = useId()

  const handleValueChange = (updates) => {
    actualOnChange({ ...normalizedValue, ...updates })
  }

  const uploadMutation = useMutation({
    mutationFn: (file) => {
      const formData = new FormData()
      formData.append('file', file)
      return api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    onSuccess: (response) => {
      const data = response.data.data
      actualOnChange({
        url: data.url,
        width: data.width || '',
        height: data.height || '',
        alt: data.alt_text || '',
      })
      setIsUploading(false)
    },
    onError: () => {
      setIsUploading(false)
    },
  })

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      setIsUploading(true)
      uploadMutation.mutate(file)
    }
  }

  const handleRemove = (e) => {
    e?.stopPropagation()
    actualOnChange({ url: '', width: '', height: '', alt: '' })
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {normalizedValue.url && displayUrl ? (
        <div className="space-y-3">
          <div className="relative">
            <img
              src={displayUrl}
              alt={normalizedValue.alt || 'Preview'}
              className="w-full h-48 object-cover rounded-lg border border-gray-300"
            />
            <button
              type="button"
              onClick={(e) => handleRemove(e)}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
            >
              ×
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Alt Text</label>
            <Input
              value={normalizedValue.alt}
              onChange={(e) => handleValueChange({ alt: e.target.value })}
              placeholder="Describe the image"
              className="w-full"
            />
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <div className="space-y-2">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="text-sm text-gray-600 space-y-2">
              <div className="flex gap-2 justify-center">
                <label htmlFor={inputId} className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                  <span className="font-medium text-indigo-600 hover:text-indigo-500">
                    Upload an image
                  </span>
                  <input
                    id={inputId}
                    name={inputId}
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />
                </label>
                <span className="text-gray-400">or</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMediaSelector(true)
                  }}
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  select from library
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
          </div>
        </div>
      )}

      {isUploading && (
        <div className="text-center text-sm text-gray-600">Uploading...</div>
      )}

      {showMediaSelector && (
        <MediaSelector
          onSelect={(mediaData) => {
            if (typeof mediaData === 'string') {
              actualOnChange({ url: mediaData, width: '', height: '', alt: '' })
            } else {
              actualOnChange({
                url: mediaData.url,
                width: mediaData.width || '',
                height: mediaData.height || '',
                alt: mediaData.alt_text || mediaData.alt || '',
              })
            }
            setShowMediaSelector(false)
          }}
          onClose={() => setShowMediaSelector(false)}
        />
      )}
    </div>
  )
}

export default ImageUpload
